import { publicApi } from "./api";

// Track if API key authentication is already in progress
let isAuthenticating = false;

/**
 * Handle API key authentication for preview mode
 * Extracts API key and event ID from URL parameters and authenticates
 */
export const handleApiKeyAuth = async () => {
  // Prevent multiple simultaneous authentication attempts
  if (isAuthenticating) {
    return false;
  }

  // Extract API key and event ID from URL query parameters
  const urlParams = new URLSearchParams(window.location.search);
  const apiKey = urlParams.get("api_key");
  const eventId = urlParams.get("event_id");

  // If no API key parameters, return false (not in preview mode)
  if (!apiKey || !eventId) {
    return false;
  }

  // Extract event slug from URL path
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const eventSlug = pathParts[1]; // /e/:eventSlug format

  if (!eventSlug) {
    return false;
  }

  // Check if we already have a session for this event
  const sessionKey = `attendee_session_${eventSlug}`;
  const existingSession = localStorage.getItem(sessionKey);
  if (existingSession) {
    try {
      const sessionData = JSON.parse(existingSession);
      if (sessionData.session_token) {
        return true;
      }
    } catch (error) {
      // Invalid session data, continue with authentication
      localStorage.removeItem(sessionKey);
    }
  }

  // Check if we're already in the middle of authentication
  const authInProgress = sessionStorage.getItem("api_key_auth_in_progress");
  if (authInProgress) {
    return false;
  }

  // Mark authentication as in progress
  sessionStorage.setItem("api_key_auth_in_progress", "true");

  isAuthenticating = true;

  try {
    // Call the API key verification endpoint
    const response = await publicApi.verifyApiKey(eventSlug, apiKey, eventId);

    // Store session data per event
    const sessionData = {
      attendee: response.attendee,
      session_token: response.session_token,
      event_slug: eventSlug,
    };

    localStorage.setItem(sessionKey, JSON.stringify(sessionData));

    // Dispatch custom event to notify AttendeeContext

    window.dispatchEvent(new CustomEvent("attendee_session_updated"));

    // Also try a small delay to ensure the event is processed
    setTimeout(() => {}, 100);

    // Clear the authentication in progress flag
    sessionStorage.removeItem("api_key_auth_in_progress");
    return true;
  } catch (error) {
    // Clear the authentication in progress flag
    sessionStorage.removeItem("api_key_auth_in_progress");
    return false;
  } finally {
    isAuthenticating = false;
  }
};

/**
 * Check if we're in API key preview mode
 */
export const isApiKeyPreviewMode = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const apiKey = urlParams.get("api_key");
  const eventId = urlParams.get("event_id");

  return !!(apiKey && eventId);
};
